/**
 * LocalizeToday - Self-hosted translation library
 * Drop-in replacement for Localize.js using LLM translation APIs
 *
 * @version 2.0.0
 * @author Sky Tech
 * @license ISC
 */

(function (root, factory) {
  if (typeof define === "function" && define.amd) {
    define([], factory);
  } else if (typeof module === "object" && module.exports) {
    module.exports = factory();
  } else {
    root.LocalizeToday = factory();
  }
}(typeof self !== "undefined" ? self : this, function () {

  "use strict";

  // Configuration
  const DEFAULT_CONFIG = {
    apiBase: "",
    apiEndpoint: "/api/translate",
    streamEndpoint: "/api/translate-stream",
    cacheTTL: 24 * 60 * 60 * 1000, // 24 hours
    autoDetectSource: true,
    sourceLanguage: "en",
    targetLanguage: null,
    languages: null,
    widget: true,
    translateAttributes: ["placeholder", "title", "alt"],
    excludedClasses: ["notranslate", "code", "pre"],
    excludedTags: ["script", "style", "code", "pre", "noscript"],
    batchSize: 500,
    whitelist: [],
    blacklist: [],
    saveNewTranslations: true,
    streaming: true,
    concurrency: 6,
    widgetTemplate: "floating",
    widgetContainer: null,
    widgetPosition: "bottom-right",
    widgetTheme: "auto",
    widgetFlags: false,
    widgetCompact: false,
    widgetShowLabel: false,
  };

  const LANGUAGE_NAMES = {
    en: "English", es: "Espa\u00f1ol", ru: "\u0420\u0443\u0441\u0441\u043a\u0438\u0439", ja: "\u65e5\u672c\u8a9e",
    fr: "Fran\u00e7ais", de: "Deutsch", it: "Italiano", pt: "Portugu\u00eas",
    zh: "\u4e2d\u6587", ko: "\ud55c\uad6d\uc5b4", ar: "\u0627\u0644\u0639\u0631\u0628\u064a\u0629", hi: "\u0939\u093f\u0928\u094d\u0926\u0940",
    pl: "Polski", nl: "Nederlands", sv: "Svenska", tr: "T\u00fcrk\u00e7e",
    uk: "\u0423\u043a\u0440\u0430\u0457\u043d\u0441\u044c\u043a\u0430", th: "\u0e44\u0e17\u0e22", vi: "Ti\u1ebfng Vi\u1ec7t", id: "Bahasa",
  };

  const LANGUAGE_FLAGS = {
    en: "\ud83c\uddec\ud83c\udde7", es: "\ud83c\uddea\ud83c\uddf8", ru: "\ud83c\uddf7\ud83c\uddfa", ja: "\ud83c\uddef\ud83c\uddf5",
    fr: "\ud83c\uddeb\ud83c\uddf7", de: "\ud83c\udde9\ud83c\uddea", it: "\ud83c\uddee\ud83c\uddf9", pt: "\ud83c\udde7\ud83c\uddf7",
    zh: "\ud83c\udde8\ud83c\uddf3", ko: "\ud83c\uddf0\ud83c\uddf7", ar: "\ud83c\uddf8\ud83c\udde6", hi: "\ud83c\uddee\ud83c\uddf3",
    pl: "\ud83c\uddf5\ud83c\uddf1", nl: "\ud83c\uddf3\ud83c\uddf1", sv: "\ud83c\uddf8\ud83c\uddea", tr: "\ud83c\uddf9\ud83c\uddf7",
    uk: "\ud83c\uddfa\ud83c\udde6", th: "\ud83c\uddf9\ud83c\udded", vi: "\ud83c\uddfb\ud83c\uddf3", id: "\ud83c\uddee\ud83c\udde9",
  };

  // State
  let config = { ...DEFAULT_CONFIG };
  let initialized = false;
  let currentLanguage = null;
  let translationsInProgress = false;
  let styleInjected = false;
  let widgetInterface = null;
  let originalTexts = new Map();
  let originalAttrs = new Map();
  let _observer = null;
  let _retranslateTimer = null;

  function getLangDisplay(lang) {
    var name = config.widgetCompact ? lang.toUpperCase() : (LANGUAGE_NAMES[lang] || lang.toUpperCase());
    if (config.widgetFlags && LANGUAGE_FLAGS[lang]) {
      name = LANGUAGE_FLAGS[lang] + " " + name;
    }
    return name;
  }

  function addKeyboardNav(container, selector, orientation) {
    container.addEventListener("keydown", function (e) {
      var items = Array.from(container.querySelectorAll(selector));
      var idx = items.indexOf(document.activeElement);
      if (idx < 0) return;
      var next = (orientation === "horizontal") ? "ArrowRight" : "ArrowDown";
      var prev = (orientation === "horizontal") ? "ArrowLeft" : "ArrowUp";
      if (e.key === next) { e.preventDefault(); items[(idx + 1) % items.length].focus(); }
      else if (e.key === prev) { e.preventDefault(); items[(idx - 1 + items.length) % items.length].focus(); }
      else if (e.key === "Escape") { container.closest(".lt-widget").querySelector(".lt-btn,.lt-dropdown-trigger")?.focus(); }
    });
  }

  /**
   * Inject CSS for translation animations
   */
  function injectStyles() {
    if (styleInjected) return;
    styleInjected = true;
    const style = document.createElement("style");
    style.textContent = `
      @keyframes lt-pulse {
        0%, 100% { opacity: 0.4; }
        50% { opacity: 0.7; }
      }
      .lt-pending {
        opacity: 0.4;
        animation: lt-pulse 1.5s ease-in-out infinite;
      }
      .lt-streaming {
        opacity: 1;
        transition: opacity 0.2s;
      }
      .lt-done {
        opacity: 1;
        transition: opacity 0.3s;
      }
    `;
    document.head.appendChild(style);
  }

  var WIDGET_CSS = {
    _base: ".lt-widget{z-index:2147483647;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;opacity:0;transform:translateY(12px) scale(0.9);animation:lt-fadein 0.4s ease forwards 0.5s;--lt-bg:#1a1a2e;--lt-text:#ccc;--lt-text-active:#fff;--lt-accent:#7eaaff;--lt-hover:#2a2a4e}" +
      "@keyframes lt-fadein{to{opacity:1;transform:translateY(0) scale(1)}}" +
      ".lt-widget *{box-sizing:border-box;margin:0;padding:0}" +
      ".lt-widget.lt-light{--lt-bg:#fff;--lt-text:#555;--lt-text-active:#111;--lt-accent:#3b82f6;--lt-hover:#f5f5f5}" +
      "@media(prefers-color-scheme:light){.lt-widget.lt-auto{--lt-bg:#fff;--lt-text:#555;--lt-text-active:#111;--lt-accent:#3b82f6;--lt-hover:#f5f5f5}}" +
      ".lt-menu{background:var(--lt-bg);border-radius:10px;box-shadow:0 4px 24px rgba(0,0,0,0.4);overflow:hidden;min-width:150px;opacity:0;transform:translateY(8px) scale(0.95);pointer-events:none;transition:opacity 0.15s,transform 0.15s}" +
      ".lt-widget.lt-light .lt-menu{box-shadow:0 4px 24px rgba(0,0,0,0.12);border:1px solid rgba(0,0,0,0.08)}" +
      ".lt-widget.lt-light .lt-btn{box-shadow:0 2px 12px rgba(0,0,0,0.1)}" +
      ".lt-menu.open{opacity:1;transform:translateY(0) scale(1);pointer-events:auto}" +
      ".lt-menu-item{display:flex;align-items:center;gap:8px;width:100%;padding:10px 16px;border:none;background:none;color:var(--lt-text);font-size:14px;text-align:left;cursor:pointer;transition:background 0.1s,color 0.1s;font-family:inherit}" +
      ".lt-menu-item:hover{background:var(--lt-hover);color:var(--lt-text-active)}" +
      ".lt-menu-item.active{color:var(--lt-accent);font-weight:600}" +
      ".lt-check{width:14px;flex-shrink:0;opacity:0}.lt-menu-item.active .lt-check{opacity:1}" +
      ".lt-progress-bar{height:2px;background:rgba(126,170,255,0.15);overflow:hidden;border-radius:1px}" +
      ".lt-progress-bar-fill{height:100%;background:var(--lt-accent);width:0;transition:width 0.3s}",
    floating: ".lt-widget.lt-floating{position:fixed;bottom:20px;right:20px}" +
      ".lt-widget.lt-floating.lt-pos-bottom-left{right:auto;left:20px}" +
      ".lt-widget.lt-floating.lt-pos-top-right{bottom:auto;top:20px}" +
      ".lt-widget.lt-floating.lt-pos-top-left{bottom:auto;top:20px;right:auto;left:20px}" +
      ".lt-widget.lt-floating.lt-pos-top-center{bottom:auto;top:20px;right:auto;left:50%;transform:translateX(-50%)}" +
      ".lt-widget.lt-floating.lt-pos-bottom-center{right:auto;left:50%;transform:translateX(-50%)}" +
      ".lt-widget.lt-floating .lt-menu{position:absolute;bottom:52px;right:0}" +
      ".lt-widget.lt-floating.lt-pos-bottom-left .lt-menu,.lt-widget.lt-floating.lt-pos-top-left .lt-menu{right:auto;left:0}" +
      ".lt-widget.lt-floating.lt-pos-top-right .lt-menu,.lt-widget.lt-floating.lt-pos-top-left .lt-menu{bottom:auto;top:52px}" +
      ".lt-widget.lt-floating.lt-pos-top-center .lt-menu{bottom:auto;top:52px;right:auto;left:50%;transform:translateX(-50%)}" +
      ".lt-widget.lt-floating.lt-pos-bottom-center .lt-menu{right:auto;left:50%;transform:translateX(-50%)}" +
      ".lt-btn{position:relative;width:44px;height:44px;border-radius:50%;border:none;background:var(--lt-bg);color:var(--lt-text);cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 12px rgba(0,0,0,0.3);transition:transform 0.2s,box-shadow 0.2s}" +
      ".lt-btn:hover{transform:scale(1.08);box-shadow:0 4px 16px rgba(0,0,0,0.4)}" +
      ".lt-btn svg.lt-globe{width:22px;height:22px}" +
      ".lt-btn-label{position:absolute;bottom:-6px;right:-6px;background:var(--lt-accent);color:#000;font-size:9px;font-weight:700;padding:1px 4px;border-radius:4px;line-height:1.2;display:none}" +
      ".lt-btn.lt-active .lt-btn-label{display:block}" +
      ".lt-btn.lt-active svg.lt-globe{stroke:var(--lt-accent)}" +
      ".lt-progress-ring{position:absolute;top:0;left:0;width:44px;height:44px;pointer-events:none}" +
      ".lt-progress-ring circle{fill:none;stroke:var(--lt-accent);stroke-width:2.5;stroke-dasharray:130;stroke-dashoffset:130;transform:rotate(-90deg);transform-origin:center;transition:stroke-dashoffset 0.3s}",
    dropdown: ".lt-widget.lt-dropdown{display:inline-flex;flex-direction:column;position:relative}" +
      ".lt-dropdown-trigger{display:inline-flex;align-items:center;gap:6px;padding:8px 14px;border-radius:8px;border:1px solid rgba(255,255,255,0.12);background:var(--lt-bg);color:var(--lt-text);cursor:pointer;font-size:14px;font-family:inherit;transition:border-color 0.15s}" +
      ".lt-widget.lt-light .lt-dropdown-trigger{border-color:rgba(0,0,0,0.12)}" +
      ".lt-dropdown-trigger:hover{border-color:var(--lt-accent)}" +
      ".lt-dropdown-trigger .lt-chevron{width:12px;height:12px;transition:transform 0.2s}" +
      ".lt-widget.lt-dropdown .lt-menu{position:absolute;top:100%;left:0;margin-top:4px;min-width:100%}" +
      ".lt-widget.lt-dropdown .lt-progress-bar{position:absolute;bottom:0;left:0;right:0;border-radius:0 0 8px 8px}",
    pills: ".lt-widget.lt-pills{display:inline-flex;flex-direction:column;gap:4px}" +
      ".lt-pills-row{display:flex;gap:6px;flex-wrap:wrap}" +
      ".lt-pill{padding:6px 14px;border-radius:20px;border:1px solid rgba(255,255,255,0.12);background:transparent;color:var(--lt-text);cursor:pointer;font-size:13px;font-family:inherit;transition:all 0.15s}" +
      ".lt-pill:hover{border-color:var(--lt-accent);color:var(--lt-text-active)}" +
      ".lt-pill.active{background:var(--lt-accent);color:#000;border-color:var(--lt-accent);font-weight:600}" +
      ".lt-widget.lt-light .lt-pill{border-color:rgba(0,0,0,0.12)}" +
      ".lt-widget.lt-light .lt-pill.active{color:#fff}",
    banner: ".lt-widget.lt-banner{position:fixed;top:0;left:0;right:0;display:flex;align-items:center;justify-content:center;gap:10px;padding:10px 20px;background:var(--lt-bg);border-bottom:1px solid rgba(255,255,255,0.08);flex-wrap:wrap}" +
      ".lt-widget.lt-banner.lt-light{border-bottom-color:rgba(0,0,0,0.08)}" +
      ".lt-widget.lt-banner.lt-pos-bottom-right,.lt-widget.lt-banner.lt-pos-bottom-left,.lt-widget.lt-banner.lt-pos-bottom-center{top:auto;bottom:0;border-bottom:none;border-top:1px solid rgba(255,255,255,0.08)}" +
      ".lt-banner-label{color:var(--lt-text);font-size:13px}" +
      ".lt-banner-lang{padding:4px 12px;border-radius:6px;border:1px solid rgba(255,255,255,0.12);background:transparent;color:var(--lt-text);cursor:pointer;font-size:13px;font-family:inherit;transition:all 0.15s}" +
      ".lt-banner-lang:hover{border-color:var(--lt-accent);color:var(--lt-text-active)}" +
      ".lt-banner-lang.active{background:var(--lt-accent);color:#000;border-color:var(--lt-accent);font-weight:600}" +
      ".lt-widget.lt-light .lt-banner-lang{border-color:rgba(0,0,0,0.12)}" +
      ".lt-widget.lt-light .lt-banner-lang.active{color:#fff}" +
      ".lt-banner-dismiss{background:none;border:none;color:var(--lt-text);cursor:pointer;font-size:18px;padding:0 4px;opacity:0.5;transition:opacity 0.15s}" +
      ".lt-banner-dismiss:hover{opacity:1}" +
      ".lt-widget.lt-banner .lt-progress-bar{position:absolute;bottom:0;left:0;right:0}",
    select: ".lt-widget.lt-select{display:inline-flex;flex-direction:column;gap:4px}" +
      ".lt-select-el{appearance:none;-webkit-appearance:none;padding:8px 32px 8px 12px;border-radius:8px;border:1px solid rgba(255,255,255,0.12);background:var(--lt-bg) url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none' stroke='%23999' stroke-width='1.5'%3E%3Cpath d='M3 5l3 3 3-3'/%3E%3C/svg%3E\") no-repeat right 10px center;color:var(--lt-text);font-size:14px;font-family:inherit;cursor:pointer;transition:border-color 0.15s;min-width:140px}" +
      ".lt-select-el:hover{border-color:var(--lt-accent)}" +
      ".lt-select-el:focus{outline:none;border-color:var(--lt-accent);box-shadow:0 0 0 2px rgba(126,170,255,0.2)}" +
      ".lt-select-el option{background:var(--lt-bg);color:var(--lt-text)}" +
      ".lt-widget.lt-light .lt-select-el{border-color:rgba(0,0,0,0.12);background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none' stroke='%23555' stroke-width='1.5'%3E%3Cpath d='M3 5l3 3 3-3'/%3E%3C/svg%3E\")}" +
      ".lt-widget.lt-select .lt-progress-bar{width:100%}",
    popup: ".lt-widget.lt-popup{position:fixed;bottom:20px;right:20px}" +
      ".lt-popup-trigger{display:inline-flex;align-items:center;gap:8px;padding:10px 16px;border-radius:24px;border:none;background:var(--lt-bg);color:var(--lt-text);cursor:pointer;font-size:14px;font-family:inherit;box-shadow:0 2px 12px rgba(0,0,0,0.3);transition:transform 0.2s,box-shadow 0.2s}" +
      ".lt-popup-trigger:hover{transform:scale(1.04);box-shadow:0 4px 16px rgba(0,0,0,0.4)}" +
      ".lt-popup-trigger svg{width:20px;height:20px}" +
      ".lt-widget.lt-light .lt-popup-trigger{box-shadow:0 2px 12px rgba(0,0,0,0.1)}" +
      ".lt-popup-overlay{display:none;position:fixed;inset:0;z-index:2147483647;background:rgba(0,0,0,0.5);align-items:center;justify-content:center}" +
      ".lt-popup-overlay.open{display:flex}" +
      ".lt-popup-panel{background:var(--lt-bg);border-radius:16px;box-shadow:0 8px 40px rgba(0,0,0,0.5);padding:24px;min-width:320px;max-width:480px;width:90vw;animation:lt-popup-in 0.2s ease}" +
      ".lt-widget.lt-light .lt-popup-panel{box-shadow:0 8px 40px rgba(0,0,0,0.15);border:1px solid rgba(0,0,0,0.08)}" +
      "@keyframes lt-popup-in{from{opacity:0;transform:scale(0.95)}to{opacity:1;transform:scale(1)}}" +
      ".lt-popup-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;font-size:16px;font-weight:600;color:var(--lt-text-active)}" +
      ".lt-popup-close{background:none;border:none;color:var(--lt-text);cursor:pointer;font-size:20px;padding:0 4px;opacity:0.6;transition:opacity 0.15s}" +
      ".lt-popup-close:hover{opacity:1}" +
      ".lt-popup-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:6px}" +
      ".lt-popup-lang{padding:10px 14px;border-radius:8px;border:1px solid rgba(255,255,255,0.08);background:transparent;color:var(--lt-text);cursor:pointer;font-size:14px;font-family:inherit;text-align:left;transition:all 0.15s}" +
      ".lt-popup-lang:hover{border-color:var(--lt-accent);color:var(--lt-text-active);background:var(--lt-hover)}" +
      ".lt-popup-lang.active{background:var(--lt-accent);color:#000;border-color:var(--lt-accent);font-weight:600}" +
      ".lt-widget.lt-light .lt-popup-lang{border-color:rgba(0,0,0,0.08)}" +
      ".lt-widget.lt-light .lt-popup-lang.active{color:#fff}" +
      ".lt-widget.lt-popup .lt-progress-bar{margin-top:12px}",
    _positions: ".lt-widget.lt-pos-bottom-right{position:fixed;bottom:20px;right:20px;left:auto;top:auto;transform:none}" +
      ".lt-widget.lt-pos-bottom-left{position:fixed;bottom:20px;left:20px;right:auto;top:auto;transform:none}" +
      ".lt-widget.lt-pos-bottom-center{position:fixed;bottom:20px;left:50%;right:auto;top:auto;transform:translateX(-50%)}" +
      ".lt-widget.lt-pos-top-right{position:fixed;top:20px;right:20px;left:auto;bottom:auto;transform:none}" +
      ".lt-widget.lt-pos-top-left{position:fixed;top:20px;left:20px;right:auto;bottom:auto;transform:none}" +
      ".lt-widget.lt-pos-top-center{position:fixed;top:20px;left:50%;right:auto;bottom:auto;transform:translateX(-50%)}" +
      ".lt-widget.lt-banner.lt-pos-top-right,.lt-widget.lt-banner.lt-pos-top-left,.lt-widget.lt-banner.lt-pos-top-center{top:0;left:0;right:0;bottom:auto;transform:none}" +
      ".lt-widget.lt-banner.lt-pos-bottom-right,.lt-widget.lt-banner.lt-pos-bottom-left,.lt-widget.lt-banner.lt-pos-bottom-center{top:auto;bottom:0;left:0;right:0;transform:none;border-top:1px solid rgba(255,255,255,0.08);border-bottom:none}" +
      ".lt-widget.lt-dropdown.lt-pos-bottom-right .lt-menu,.lt-widget.lt-dropdown.lt-pos-bottom-left .lt-menu,.lt-widget.lt-dropdown.lt-pos-bottom-center .lt-menu{top:auto;bottom:100%;margin-top:0;margin-bottom:4px}" +
      ".lt-widget.lt-floating.lt-pos-top-right .lt-menu,.lt-widget.lt-floating.lt-pos-top-left .lt-menu,.lt-widget.lt-floating.lt-pos-top-center .lt-menu{bottom:auto;top:52px}" +
      ".lt-widget.lt-pos-bottom-left .lt-menu,.lt-widget.lt-pos-top-left .lt-menu{right:auto;left:0}" +
      ".lt-widget.lt-pos-bottom-center .lt-menu,.lt-widget.lt-pos-top-center .lt-menu{right:auto;left:50%;transform:translateX(-50%)}"
  };

  function injectWidgetStyles(template) {
    const style = document.createElement("style");
    style.textContent = WIDGET_CSS._base + (WIDGET_CSS[template] || WIDGET_CSS.floating) + WIDGET_CSS._positions;
    document.head.appendChild(style);
  }

  var GLOBE_SVG = '<svg class="lt-globe" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z"/></svg>';
  var CHECK_SVG = '<svg class="lt-check" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 7l3.5 3.5L12 4"/></svg>';
  var CHEVRON_SVG = '<svg class="lt-chevron" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 5l3 3 3-3"/></svg>';

  function buildMenuItem(lang, handler) {
    var btn = document.createElement("button");
    btn.className = "lt-menu-item";
    btn.dataset.lang = lang;
    btn.innerHTML = CHECK_SVG + "<span>" + getLangDisplay(lang) + "</span>";
    btn.addEventListener("click", function () { handler(lang); });
    return btn;
  }

  function buildMenuList(onSelect) {
    var menu = document.createElement("div");
    menu.className = "lt-menu";
    menu.setAttribute("role", "listbox");
    for (var i = 0; i < config.languages.length; i++) {
      menu.appendChild(buildMenuItem(config.languages[i], onSelect));
    }
    addKeyboardNav(menu, ".lt-menu-item", "vertical");
    return menu;
  }

  function menuSetActive(menu, lang) {
    var items = menu.querySelectorAll(".lt-menu-item");
    items.forEach(function (item) { item.classList.toggle("active", item.dataset.lang === lang); });
  }

  function selectHandler(lang, menu) {
    if (menu) menu.classList.remove("open");
    if (lang === config.sourceLanguage) { untranslate(); }
    else { setLanguage(lang); }
  }

  function buildFloatingWidget() {
    var container = document.createElement("div");
    var pos = config.widgetPosition || "bottom-right";
    container.className = "lt-widget lt-floating lt-pos-" + pos + " lt-" + (config.widgetTheme || "auto") + " notranslate";

    var menu = buildMenuList(function (lang) { selectHandler(lang, menu); });
    var globe = document.createElement("button");
    globe.className = "lt-btn";
    globe.setAttribute("aria-label", "Translate page");
    globe.innerHTML = GLOBE_SVG + '<svg class="lt-progress-ring" viewBox="0 0 44 44"><circle cx="22" cy="22" r="20.5"/></svg><span class="lt-btn-label"></span>';
    globe.addEventListener("click", function (e) { e.stopPropagation(); menu.classList.toggle("open"); });

    container.appendChild(menu);
    container.appendChild(globe);

    return {
      element: container,
      setActive: function (lang) {
        menuSetActive(menu, lang);
        var isTranslated = lang !== config.sourceLanguage;
        globe.classList.toggle("lt-active", isTranslated);
        var lbl = globe.querySelector(".lt-btn-label");
        if (lbl) {
          if (config.widgetFlags && LANGUAGE_FLAGS[lang]) {
            lbl.textContent = LANGUAGE_FLAGS[lang];
          } else {
            lbl.textContent = lang.toUpperCase();
          }
        }
      },
      setProgress: function (f) {
        var circle = container.querySelector(".lt-progress-ring circle");
        if (circle) circle.style.strokeDashoffset = 130 * (1 - f);
      }
    };
  }

  function buildDropdownWidget() {
    var container = document.createElement("div");
    var pos = config.widgetPosition || "bottom-right";
    container.className = "lt-widget lt-dropdown lt-pos-" + pos + " lt-" + (config.widgetTheme || "auto") + " notranslate";

    var trigger = document.createElement("button");
    trigger.className = "lt-dropdown-trigger";
    trigger.innerHTML = '<span class="lt-dropdown-label"></span>' + CHEVRON_SVG;

    var menu = buildMenuList(function (lang) { selectHandler(lang, menu); });
    var bar = document.createElement("div");
    bar.className = "lt-progress-bar";
    bar.innerHTML = '<div class="lt-progress-bar-fill"></div>';

    trigger.addEventListener("click", function (e) { e.stopPropagation(); menu.classList.toggle("open"); });

    container.appendChild(trigger);
    container.appendChild(menu);
    container.appendChild(bar);

    return {
      element: container,
      setActive: function (lang) {
        menuSetActive(menu, lang);
        trigger.querySelector(".lt-dropdown-label").textContent = getLangDisplay(lang);
      },
      setProgress: function (f) {
        bar.querySelector(".lt-progress-bar-fill").style.width = (f * 100) + "%";
      }
    };
  }

  function buildPillsWidget() {
    var container = document.createElement("div");
    var pos = config.widgetPosition || "bottom-right";
    container.className = "lt-widget lt-pills lt-pos-" + pos + " lt-" + (config.widgetTheme || "auto") + " notranslate";

    var row = document.createElement("div");
    row.className = "lt-pills-row";
    row.setAttribute("role", "radiogroup");

    for (var i = 0; i < config.languages.length; i++) {
      (function (lang) {
        var pill = document.createElement("button");
        pill.className = "lt-pill";
        pill.dataset.lang = lang;
        pill.setAttribute("role", "radio");
        pill.textContent = getLangDisplay(lang);
        pill.addEventListener("click", function () {
          if (lang === config.sourceLanguage) { untranslate(); }
          else { setLanguage(lang); }
        });
        row.appendChild(pill);
      })(config.languages[i]);
    }
    addKeyboardNav(row, ".lt-pill", "horizontal");

    var bar = document.createElement("div");
    bar.className = "lt-progress-bar";
    bar.innerHTML = '<div class="lt-progress-bar-fill"></div>';

    container.appendChild(row);
    container.appendChild(bar);

    return {
      element: container,
      setActive: function (lang) {
        row.querySelectorAll(".lt-pill").forEach(function (p) {
          var isActive = p.dataset.lang === lang;
          p.classList.toggle("active", isActive);
          p.setAttribute("aria-checked", isActive);
        });
      },
      setProgress: function (f) {
        bar.querySelector(".lt-progress-bar-fill").style.width = (f * 100) + "%";
      }
    };
  }

  function buildBannerWidget() {
    var container = document.createElement("div");
    var pos = config.widgetPosition || "bottom-right";
    container.className = "lt-widget lt-banner lt-pos-" + pos + " lt-" + (config.widgetTheme || "auto") + " notranslate";

    var label = document.createElement("span");
    label.className = "lt-banner-label";
    label.textContent = "View in:";
    container.appendChild(label);

    for (var i = 0; i < config.languages.length; i++) {
      (function (lang) {
        var btn = document.createElement("button");
        btn.className = "lt-banner-lang";
        btn.dataset.lang = lang;
        btn.textContent = getLangDisplay(lang);
        btn.addEventListener("click", function () {
          if (lang === config.sourceLanguage) { untranslate(); }
          else { setLanguage(lang); }
          try { sessionStorage.setItem("lt_banner_dismissed", "1"); } catch (e) {}
          container.style.display = "none";
        });
        container.appendChild(btn);
      })(config.languages[i]);
    }

    var dismiss = document.createElement("button");
    dismiss.className = "lt-banner-dismiss";
    dismiss.innerHTML = "\u00d7";
    dismiss.setAttribute("aria-label", "Dismiss");
    dismiss.addEventListener("click", function () {
      try { sessionStorage.setItem("lt_banner_dismissed", "1"); } catch (e) {}
      container.style.display = "none";
    });
    container.appendChild(dismiss);

    var bar = document.createElement("div");
    bar.className = "lt-progress-bar";
    bar.innerHTML = '<div class="lt-progress-bar-fill"></div>';
    container.appendChild(bar);

    try {
      if (sessionStorage.getItem("lt_banner_dismissed")) container.style.display = "none";
    } catch (e) {}

    return {
      element: container,
      setActive: function (lang) {
        container.querySelectorAll(".lt-banner-lang").forEach(function (b) {
          b.classList.toggle("active", b.dataset.lang === lang);
        });
      },
      setProgress: function (f) {
        bar.querySelector(".lt-progress-bar-fill").style.width = (f * 100) + "%";
      }
    };
  }

  function buildSelectWidget() {
    var container = document.createElement("div");
    var pos = config.widgetPosition || "bottom-right";
    container.className = "lt-widget lt-select lt-pos-" + pos + " lt-" + (config.widgetTheme || "auto") + " notranslate";

    var sel = document.createElement("select");
    sel.className = "lt-select-el";
    sel.setAttribute("aria-label", "Choose language");

    for (var i = 0; i < config.languages.length; i++) {
      var opt = document.createElement("option");
      opt.value = config.languages[i];
      opt.textContent = getLangDisplay(config.languages[i]);
      sel.appendChild(opt);
    }

    sel.addEventListener("change", function () {
      var lang = sel.value;
      if (lang === config.sourceLanguage) { untranslate(); }
      else { setLanguage(lang); }
    });

    var bar = document.createElement("div");
    bar.className = "lt-progress-bar";
    bar.innerHTML = '<div class="lt-progress-bar-fill"></div>';

    container.appendChild(sel);
    container.appendChild(bar);

    return {
      element: container,
      setActive: function (lang) {
        sel.value = lang;
      },
      setProgress: function (f) {
        bar.querySelector(".lt-progress-bar-fill").style.width = (f * 100) + "%";
      }
    };
  }

  function buildPopupWidget() {
    var container = document.createElement("div");
    var pos = config.widgetPosition || "bottom-right";
    container.className = "lt-widget lt-popup lt-pos-" + pos + " lt-" + (config.widgetTheme || "auto") + " notranslate";

    var trigger = document.createElement("button");
    trigger.className = "lt-popup-trigger";
    trigger.setAttribute("aria-label", "Choose language");
    trigger.innerHTML = GLOBE_SVG + '<span class="lt-popup-trigger-label">' + getLangDisplay(config.sourceLanguage) + "</span>";

    var overlay = document.createElement("div");
    overlay.className = "lt-popup-overlay";

    var panel = document.createElement("div");
    panel.className = "lt-popup-panel";

    var header = document.createElement("div");
    header.className = "lt-popup-header";
    header.innerHTML = '<span>Choose language</span><button class="lt-popup-close">\u00d7</button>';

    var grid = document.createElement("div");
    grid.className = "lt-popup-grid";

    for (var i = 0; i < config.languages.length; i++) {
      (function (lang) {
        var btn = document.createElement("button");
        btn.className = "lt-popup-lang";
        btn.dataset.lang = lang;
        btn.textContent = getLangDisplay(lang);
        btn.addEventListener("click", function () {
          overlay.classList.remove("open");
          if (lang === config.sourceLanguage) { untranslate(); }
          else { setLanguage(lang); }
        });
        grid.appendChild(btn);
      })(config.languages[i]);
    }

    var bar = document.createElement("div");
    bar.className = "lt-progress-bar";
    bar.innerHTML = '<div class="lt-progress-bar-fill"></div>';

    panel.appendChild(header);
    panel.appendChild(grid);
    panel.appendChild(bar);
    overlay.appendChild(panel);
    container.appendChild(trigger);
    container.appendChild(overlay);

    // Open
    trigger.addEventListener("click", function (e) {
      e.stopPropagation();
      overlay.classList.add("open");
    });

    // Close via button
    header.querySelector(".lt-popup-close").addEventListener("click", function () {
      overlay.classList.remove("open");
    });

    // Close via overlay backdrop
    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) overlay.classList.remove("open");
    });

    // Close via Escape
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && overlay.classList.contains("open")) {
        overlay.classList.remove("open");
        trigger.focus();
      }
    });

    addKeyboardNav(grid, ".lt-popup-lang", "horizontal");

    return {
      element: container,
      setActive: function (lang) {
        grid.querySelectorAll(".lt-popup-lang").forEach(function (b) {
          b.classList.toggle("active", b.dataset.lang === lang);
        });
        trigger.querySelector(".lt-popup-trigger-label").textContent = getLangDisplay(lang);
      },
      setProgress: function (f) {
        bar.querySelector(".lt-progress-bar-fill").style.width = (f * 100) + "%";
      }
    };
  }

  var builders = {
    floating: buildFloatingWidget,
    dropdown: buildDropdownWidget,
    pills: buildPillsWidget,
    banner: buildBannerWidget,
    select: buildSelectWidget,
    popup: buildPopupWidget,
  };

  function createWidget() {
    if (!config.widget || !config.languages || config.languages.length === 0) return;
    var template = config.widgetTemplate || "floating";
    injectWidgetStyles(template);
    var builder = builders[template] || builders.floating;
    widgetInterface = builder();
    var target = config.widgetContainer
      ? document.querySelector(config.widgetContainer) : document.body;
    if (target) target.appendChild(widgetInterface.element);

    // Close menus on outside click (for floating/dropdown/popup)
    if (template === "floating" || template === "dropdown") {
      document.addEventListener("click", function (e) {
        if (!widgetInterface.element.contains(e.target)) {
          var m = widgetInterface.element.querySelector(".lt-menu");
          if (m) m.classList.remove("open");
        }
      });
    }
  }

  function updateWidgetState(lang) {
    if (widgetInterface && widgetInterface.setActive) widgetInterface.setActive(lang);
  }

  function updateWidgetProgress(fraction) {
    if (widgetInterface && widgetInterface.setProgress) widgetInterface.setProgress(fraction);
  }

  /**
   * Get localStorage key for cache
   */
  function getCacheKey(text, targetLang) {
    const hash = simpleHash(text);
    return `lt_cache:${targetLang}:${hash}`;
  }

  /**
   * Simple hash function
   */
  function simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16);
  }

  /**
   * Get cached translation
   */
  function getCachedTranslation(text, targetLang) {
    try {
      const key = getCacheKey(text, targetLang);
      const entry = localStorage.getItem(key);
      if (!entry) return null;

      const data = JSON.parse(entry);
      if (data.expiresAt && data.expiresAt < Date.now()) {
        localStorage.removeItem(key);
        return null;
      }

      return data.translation;
    } catch {
      return null;
    }
  }

  /**
   * Set cached translation
   */
  function setCachedTranslation(text, targetLang, translation) {
    try {
      const key = getCacheKey(text, targetLang);
      const entry = {
        translation,
        cachedAt: Date.now(),
        expiresAt: Date.now() + config.cacheTTL,
      };
      localStorage.setItem(key, JSON.stringify(entry));
    } catch (e) {
      if (e.name === "QuotaExceededError") {
        console.warn("LocalizeToday: Cache quota exceeded");
      }
    }
  }

  /**
   * Detect browser language
   */
  function detectBrowserLanguage() {
    if (typeof navigator === "undefined") return DEFAULT_CONFIG.sourceLanguage;
    const lang = navigator.language || navigator.userLanguage;
    return lang ? lang.split("-")[0] : DEFAULT_CONFIG.sourceLanguage;
  }

  /**
   * Detect source language from text
   */
  function detectSourceLanguage(text) {
    const patterns = {
      en: /\b(the|is|and|or|but|in|on|at|to|for|of|with|a|an)\b/i,
      es: /\b(el|la|es|en|y|o|pero|por|para|con|de|un|una|los|las)\b/i,
      fr: /\b(le|la|les|est|et|ou|mais|dans|pour|avec|de|un|une|des)\b/i,
      de: /\b(der|die|das|ist|und|oder|aber|in|an|zu|für|mit|von|ein|eine)\b/i,
      it: /\b(il|la|lo|è|e|o|ma|in|a|per|con|di|un|uno|una|i|gli)\b/i,
      pt: /\b(o|a|os|as|é|e|ou|mas|em|a|para|com|de|um|uma|uns|umas)\b/i,
      ru: /\b(и|в|на|или|но|с|к|для|а|не|один|одна|одни|это|то)\b/i,
      ja: /[\u3040-\u309F\u30A0-\u30FF]/,
      zh: /[\u4E00-\u9FFF]/,
      ko: /[\uAC00-\uD7AF\u1100-\u11FF]/
    };

    let bestMatch = DEFAULT_CONFIG.sourceLanguage;
    let maxScore = 0;

    for (const [lang, pattern] of Object.entries(patterns)) {
      const matches = text.match(pattern);
      const score = matches ? matches.length : 0;
      if (score > maxScore) {
        maxScore = score;
        bestMatch = lang;
      }
    }

    return bestMatch;
  }

  /**
   * Check if element should be translated
   */
  function shouldTranslateElement(element) {
    const tagName = element.tagName.toLowerCase();
    if (config.excludedTags.includes(tagName)) {
      return false;
    }

    // Respect HTML translate="no" attribute
    if (element.closest("[translate='no']")) {
      return false;
    }

    for (const className of element.classList) {
      if (config.excludedClasses.some(excl => className.includes(excl))) {
        return false;
      }
    }

    for (const selector of config.blacklist) {
      if (element.matches(selector) || element.closest(selector)) {
        return false;
      }
    }

    if (config.whitelist.length > 0) {
      return config.whitelist.some(selector => element.matches(selector));
    }

    return true;
  }

  // Block-level tags whose full text should be translated as a unit
  const BLOCK_TAGS = new Set(["p", "li", "h1", "h2", "h3", "h4", "h5", "h6", "td", "th", "dt", "dd", "figcaption", "blockquote", "button", "label", "legend", "summary", "caption"]);

  /**
   * Extract translatable block elements (p, li, h2, etc.),
   * standalone nav links, and translatable attributes (placeholder, title, alt)
   */
  function extractTextNodes(element) {
    const items = [];
    const seen = new Set();
    const blocks = element.querySelectorAll(
      Array.from(BLOCK_TAGS).join(",")
    );

    for (const block of blocks) {
      if (!shouldTranslateElement(block)) continue;

      const text = block.textContent.trim();
      if (!text) continue;
      seen.add(block);

      // Blocks with links/media: translate text inside links individually
      // instead of skipping — setting textContent on the block would destroy HTML
      if (block.querySelector("a, img, video, iframe")) {
        var links = block.querySelectorAll("a");
        for (var j = 0; j < links.length; j++) {
          var link = links[j];
          if (!shouldTranslateElement(link)) continue;
          seen.add(link);
          var textEls = link.querySelectorAll("span, strong, em, b, label");
          var added = false;
          for (var k = 0; k < textEls.length; k++) {
            var el = textEls[k];
            var t = el.textContent.trim();
            if (t && !el.querySelector("a, img, video, iframe, svg")) {
              items.push({ node: el, text: t, isBlock: true, leadingSpace: "", trailingSpace: "" });
              added = true;
            }
          }
          // Pure text link with no child elements
          if (!added && link.children.length === 0) {
            var lt = link.textContent.trim();
            if (lt) {
              items.push({ node: link, text: lt, isBlock: true, leadingSpace: "", trailingSpace: "" });
            }
          }
        }
        continue;
      }

      items.push({
        node: block,
        text,
        isBlock: true,
        leadingSpace: "",
        trailingSpace: "",
      });
    }

    // Standalone <a> links not already captured (nav links, header links, etc.)
    var allLinks = element.querySelectorAll("a");
    for (var li = 0; li < allLinks.length; li++) {
      var a = allLinks[li];
      if (seen.has(a)) continue;
      if (!shouldTranslateElement(a)) continue;
      if (a.children.length > 0) continue; // Skip links with child elements (icons, images)
      var aText = a.textContent.trim();
      if (aText && aText.length < 200) {
        items.push({ node: a, text: aText, isBlock: true, leadingSpace: "", trailingSpace: "" });
      }
    }

    // Translatable attributes: placeholder, title, alt
    if (config.translateAttributes && config.translateAttributes.length > 0) {
      var attrSelector = config.translateAttributes.map(function(attr) {
        return "[" + attr + "]";
      }).join(",");
      var attrEls = element.querySelectorAll(attrSelector);
      for (var ai = 0; ai < attrEls.length; ai++) {
        var attrEl = attrEls[ai];
        if (!shouldTranslateElement(attrEl)) continue;
        for (var aj = 0; aj < config.translateAttributes.length; aj++) {
          var attrName = config.translateAttributes[aj];
          var attrVal = attrEl.getAttribute(attrName);
          if (attrVal && attrVal.trim()) {
            items.push({
              node: attrEl,
              text: attrVal.trim(),
              isBlock: true,
              leadingSpace: "",
              trailingSpace: "",
              attr: attrName,
            });
          }
        }
      }
    }

    return items;
  }

  /**
   * Batch texts for API efficiency
   */
  function batchTexts(textItems, maxSize) {
    const batches = [];
    let currentBatch = [];
    let currentLength = 0;

    for (const item of textItems) {
      const textLength = item.text.length;

      if ((currentLength + textLength > maxSize || currentBatch.length >= 50) && currentBatch.length > 0) {
        batches.push(currentBatch);
        currentBatch = [item];
        currentLength = textLength;
      } else {
        currentBatch.push(item);
        currentLength += textLength;
      }
    }

    if (currentBatch.length > 0) {
      batches.push(currentBatch);
    }

    return batches;
  }

  /**
   * Call batch translation API
   */
  // Shared by initial, dynamic and fallback passes. No automatic retry timer:
  // a later user action or DOM change may try again after the cooldown.
  let providerRetryAt = 0;
  function providerUnavailable() { return Date.now() < providerRetryAt; }
  function pauseProvider(response) {
    const retry = response?.headers.get("Retry-After");
    let delay = Number(retry) * 1000;
    if (retry && !Number.isFinite(delay)) delay = Date.parse(retry) - Date.now();
    if (!Number.isFinite(delay) || delay <= 0) delay = 30000;
    providerRetryAt = Math.max(providerRetryAt, Date.now() + Math.min(delay, 3600000));
  }

  async function translationRequest(endpoint, body, consume) {
    if (providerUnavailable()) throw new Error("Translation provider cooling down");
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!response.ok) {
        if ([401, 402, 403, 429].includes(response.status) || response.status >= 500) pauseProvider(response);
        await response.body?.cancel();
        const error = new Error(`Translation API error: ${response.status}`);
        error.httpStatus = response.status;
        throw error;
      }
      return await consume(response);
    } catch (error) {
      // Network errors, timeouts and broken provider payloads must not fan out.
      if (!error.httpStatus) pauseProvider();
      throw error;
    } finally {
      clearTimeout(timeout);
    }
  }

  async function translateAPI(texts, sourceLang, targetLang) {
    const translations = [];
    for (let offset = 0; offset < texts.length; offset += 50) {
      const chunk = texts.slice(offset, offset + 50);
      const result = await translationRequest(config.apiEndpoint, {
        texts: chunk, source: sourceLang, target: targetLang,
      }, async response => {
        const data = await response.json();
        if (!Array.isArray(data.translations) || data.translations.length !== chunk.length ||
            data.translations.some(text => typeof text !== "string")) {
          throw new Error("Invalid translation response");
        }
        return data.translations;
      });
      translations.push(...result);
    }
    return translations;
  }

  /**
   * Apply a finished translation to a DOM item (text or attribute)
   */
  function applyTranslation(item, translation, targetLang) {
    if (item.attr) {
      item.node.setAttribute(item.attr, translation);
    } else {
      item.node.textContent = translation;
      var el = item.isBlock ? item.node : item.node.parentElement;
      if (el) el.classList.add("lt-done");
    }
    if (config.saveNewTranslations) {
      setCachedTranslation(item.text, targetLang, translation);
    }
  }

  /**
   * Stream translation for a single text node — token by token into DOM
   */
  async function streamTranslateNode(item, sourceLang, targetLang) {
    const el = item.attr ? null : (item.isBlock ? item.node : item.node.parentElement);
    // Preserve inline markup if a partial stream must be rolled back.
    const original = item.attr ? item.node.getAttribute(item.attr) :
      (item.isBlock ? item.node.innerHTML : item.node.textContent);
    if (el) el.classList.add("lt-streaming");
    try {
      return await translationRequest(config.streamEndpoint, {
        text: item.text, source: sourceLang, target: targetLang,
      }, async response => {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let accumulated = "";
        let completed = false;
        try {
          while (!completed) {
            const { done, value } = await reader.read();
            if (done) throw new Error("Translation stream ended before DONE");
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop();
            for (const line of lines) {
              if (!line.startsWith("data:")) continue;
              const data = line.slice(5).trim();
              if (data === "[DONE]") { completed = true; break; }
              const parsed = JSON.parse(data);
              if (parsed.error) throw new Error("Translation stream failed");
              if (typeof parsed.token === "string") {
                accumulated += parsed.token;
                if (item.attr) item.node.setAttribute(item.attr, accumulated);
                else item.node.textContent = item.isBlock ? accumulated : item.leadingSpace + accumulated + item.trailingSpace;
              }
            }
          }
          if (!accumulated) throw new Error("Empty translation stream");
          if (el) el.classList.add("lt-done");
          if (config.saveNewTranslations) setCachedTranslation(item.text, targetLang, accumulated);
          return accumulated;
        } finally {
          await reader.cancel().catch(() => {});
          reader.releaseLock();
        }
      });
    } catch {
      if (item.attr) item.node.setAttribute(item.attr, original);
      else if (item.isBlock) item.node.innerHTML = original;
      else item.node.textContent = original;
      return null;
    } finally {
      if (el) el.classList.remove("lt-streaming");
    }
  }

  /**
   * Emit progress event
   */
  function fireProgressEvent(done, total) {
    const event = new CustomEvent("localize:progress", {
      detail: { done, total, language: currentLanguage }
    });
    document.dispatchEvent(event);
  }

  /**
   * Start MutationObserver for dynamically loaded content
   */
  function startObserver() {
    if (_observer || typeof MutationObserver === "undefined") return;
    _observer = new MutationObserver(function (mutations) {
      if (currentLanguage === config.sourceLanguage) return;
      var hasNew = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length > 0) { hasNew = true; break; }
      }
      if (!hasNew) return;
      clearTimeout(_retranslateTimer);
      _retranslateTimer = setTimeout(translateDynamic, 300);
    });
    _observer.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Translate only newly added DOM content (dynamic pages)
   */
  async function translateDynamic() {
    if (translationsInProgress || providerUnavailable()) return;
    if (!currentLanguage || currentLanguage === config.sourceLanguage) return;

    const targetLang = currentLanguage;
    const textItems = extractTextNodes(document.body);
    const newItems = textItems.filter(function (item) {
      if (item.attr) return !originalAttrs.has("attr:" + item.attr + ":" + item.text);
      return !originalTexts.has(item.node);
    });
    if (newItems.length === 0) return;

    translationsInProgress = true;
    injectStyles();

    try {
      var sourceLang = config.sourceLanguage;

      for (const item of newItems) {
        if (item.attr) {
          originalAttrs.set("attr:" + item.attr + ":" + item.text, { node: item.node, attr: item.attr, value: item.node.getAttribute(item.attr) });
        } else {
          originalTexts.set(item.node, item.isBlock ? item.node.innerHTML : item.node.textContent);
        }
      }

      const toTranslate = [];
      for (const item of newItems) {
        const cached = getCachedTranslation(item.text, targetLang);
        if (cached) {
          if (item.attr) {
            item.node.setAttribute(item.attr, cached);
          } else {
            item.node.textContent = cached;
            const el = item.isBlock ? item.node : item.node.parentElement;
            if (el) el.classList.add("lt-done");
          }
        } else {
          toTranslate.push(item);
          if (!item.attr) {
            const el = item.isBlock ? item.node : item.node.parentElement;
            if (el) el.classList.add("lt-pending");
          }
        }
      }

      if (toTranslate.length === 0) return;

      console.log("LocalizeToday: Translating " + toTranslate.length + " dynamic elements");

      if (config.streaming) {
        let index = 0;
        var failed = [];
        async function next() {
          while (index < toTranslate.length && !providerUnavailable()) {
            const item = toTranslate[index++];
            const el = item.isBlock ? item.node : item.node.parentElement;
            if (el) el.classList.remove("lt-pending");
            var result = await streamTranslateNode(item, sourceLang, targetLang);
            if (!result) failed.push(item);
          }
        }
        const workers = [];
        for (let w = 0; w < Math.min(config.concurrency || 6, toTranslate.length); w++) {
          workers.push(next());
        }
        await Promise.all(workers);
        // Retry failed items via single batch API call
        if (failed.length > 0 && !providerUnavailable()) {
          console.log("LocalizeToday: Retrying " + failed.length + " dynamic items via batch");
          try {
            var texts = failed.map(function(item) { return item.text; });
            var translations = await translateAPI(texts, sourceLang, targetLang);
            for (var fi = 0; fi < failed.length; fi++) {
              if (translations[fi]) {
                applyTranslation(failed[fi], translations[fi], targetLang);
              }
            }
          } catch (err) {
            console.warn("LocalizeToday: Batch retry failed", err);
          }
        }
      } else {
        const batches = batchTexts(toTranslate, config.batchSize);
        for (const batch of batches) {
          if (providerUnavailable()) break;
          try {
            const texts = batch.map(function (item) { return item.text; });
            const translated = await translateAPI(texts, sourceLang, targetLang);
            for (let i = 0; i < batch.length; i++) {
              if (!batch[i].attr) {
                const el = batch[i].isBlock ? batch[i].node : batch[i].node.parentElement;
                if (el) el.classList.remove("lt-pending");
              }
              if (translated[i]) {
                applyTranslation(batch[i], translated[i], targetLang);
              }
            }
          } catch (error) {
            console.error("LocalizeToday: Dynamic translation batch failed", error);
          }
        }
      }
    } finally {
      document.querySelectorAll(".lt-pending,.lt-streaming").forEach(el => el.classList.remove("lt-pending", "lt-streaming"));
      updateWidgetProgress(0);
      translationsInProgress = false;
    }
  }

  /**
   * Translate a page
   */
  async function translate() {
    if (!initialized) {
      console.warn("LocalizeToday: Not initialized. Call initialize() first.");
      return;
    }

    if (translationsInProgress || providerUnavailable()) {
      console.warn("LocalizeToday: Translation already in progress");
      return;
    }

    translationsInProgress = true;
    injectStyles();

    try {
      const targetLang = currentLanguage || config.targetLanguage;
      if (!targetLang) {
        console.warn("LocalizeToday: No target language set");
        return;
      }

      // Never translate to the page's original language
      if (targetLang === config.sourceLanguage) {
        untranslate();
        return;
      }

      const body = document.body;
      const textItems = extractTextNodes(body);

      if (textItems.length === 0) {
        console.log("LocalizeToday: No translatable text found");
        return;
      }

      let sourceLang = config.sourceLanguage;
      if (config.autoDetectSource && textItems.length > 0) {
        const sampleText = textItems.slice(0, 5).map(t => t.text).join(" ");
        sourceLang = detectSourceLanguage(sampleText);
      }

      // Save originals on first translation pass
      for (const item of textItems) {
        if (item.attr) {
          // Use "attr:attrName" suffix on node key to separate from textContent originals
          var attrKey = "attr:" + item.attr + ":" + item.text;
          if (!originalAttrs.has(attrKey)) {
            originalAttrs.set(attrKey, { node: item.node, attr: item.attr, value: item.node.getAttribute(item.attr) });
          }
        } else if (!originalTexts.has(item.node)) {
          originalTexts.set(item.node, item.isBlock ? item.node.innerHTML : item.node.textContent);
        }
      }

      // Separate cached and uncached
      const toTranslate = [];

      for (const item of textItems) {
        const cached = getCachedTranslation(item.text, targetLang);
        if (cached) {
          // Apply cached immediately
          if (item.attr) {
            item.node.setAttribute(item.attr, cached);
          } else if (item.isBlock) {
            item.node.textContent = cached;
          } else {
            item.node.textContent = item.leadingSpace + cached + item.trailingSpace;
          }
          if (!item.attr) {
            const el = item.isBlock ? item.node : item.node.parentElement;
            if (el) el.classList.add("lt-done");
          }
        } else {
          toTranslate.push(item);
          // Dim uncached text to signal pending translation
          if (!item.attr) {
            const el = item.isBlock ? item.node : item.node.parentElement;
            if (el) el.classList.add("lt-pending");
          }
        }
      }

      console.log(`LocalizeToday: ${textItems.length - toTranslate.length} cached, ${toTranslate.length} to translate`);

      if (toTranslate.length === 0) {
        fireTranslatedEvent();
        return;
      }

      fireProgressEvent(0, toTranslate.length);
      updateWidgetProgress(0);

      // Streaming mode: translate each node with streaming tokens
      if (config.streaming) {
        let done = 0;
        const concurrency = config.concurrency || 6;
        let index = 0;
        const failed = [];

        async function next() {
          while (index < toTranslate.length && !providerUnavailable()) {
            const item = toTranslate[index++];
            const el = item.isBlock ? item.node : item.node.parentElement;
            if (el) el.classList.remove("lt-pending");

            var result = await streamTranslateNode(item, sourceLang, targetLang);
            if (!result) failed.push(item);
            done++;
            fireProgressEvent(done, toTranslate.length);
            updateWidgetProgress(done / toTranslate.length);
          }
        }

        const workers = [];
        for (let w = 0; w < Math.min(concurrency, toTranslate.length); w++) {
          workers.push(next());
        }
        await Promise.all(workers);

        // Retry failed items via single batch API call (faster than N sequential streams)
        if (failed.length > 0 && !providerUnavailable()) {
          console.log("LocalizeToday: Retrying " + failed.length + " via batch");
          try {
            var texts = failed.map(function(item) { return item.text; });
            var translations = await translateAPI(texts, sourceLang, targetLang);
            for (var i = 0; i < failed.length; i++) {
              if (translations[i]) {
                applyTranslation(failed[i], translations[i], targetLang);
              }
            }
          } catch (err) {
            console.warn("LocalizeToday: Batch retry failed", err);
          }
        }

      } else {
        // Batch mode: translate in batches, apply each batch immediately
        const batches = batchTexts(toTranslate, config.batchSize);
        let done = 0;

        for (const batch of batches) {
          if (providerUnavailable()) break;
          try {
            const texts = batch.map(item => item.text);
            const translated = await translateAPI(texts, sourceLang, targetLang);

            for (let i = 0; i < batch.length; i++) {
              const item = batch[i];
              const translation = translated[i];

              if (!item.attr) {
                const el = item.isBlock ? item.node : item.node.parentElement;
                if (el) el.classList.remove("lt-pending");
              }

              if (translation) {
                applyTranslation(item, translation, targetLang);
              }
              done++;
            }

            fireProgressEvent(done, toTranslate.length);
          } catch (error) {
            console.error("LocalizeToday: Translation batch failed", error);
            done += batch.length;
          }
        }
      }

      updateWidgetProgress(1);
      setTimeout(() => updateWidgetProgress(0), 1000);
      if (providerUnavailable()) {
        document.dispatchEvent(new CustomEvent("localize:error", { detail: { code: "translation_provider_unavailable", retryAt: providerRetryAt } }));
      } else {
        fireTranslatedEvent();
      }

    } finally {
      document.querySelectorAll(".lt-pending,.lt-streaming").forEach(el => el.classList.remove("lt-pending", "lt-streaming"));
      updateWidgetProgress(0);
      translationsInProgress = false;
    }
  }

  /**
   * Set language
   */
  function setLanguage(language) {
    currentLanguage = language;
    config.targetLanguage = language;
    try { localStorage.setItem("lt_lang", language); } catch {}
    updateWidgetState(language);

    if (initialized) {
      restoreOriginals();
      // Don't translate to the page's original language
      if (language !== config.sourceLanguage) {
        translate();
      }
    }
  }

  /**
   * Get current language
   */
  function getLanguage() {
    return currentLanguage || config.targetLanguage;
  }

  /**
   * Initialize the library
   */
  function initialize(userConfig = {}) {
    if (initialized) {
      console.warn("LocalizeToday: Already initialized");
      return;
    }

    // Merge user config
    config = { ...config, ...userConfig };

    // Detect page language from <html lang> attribute (no AI)
    if (!userConfig.sourceLanguage) {
      var htmlLang = document.documentElement.getAttribute("lang");
      if (htmlLang) {
        config.sourceLanguage = htmlLang.split("-")[0].toLowerCase();
      }
    }

    // Normalize widget object shorthand: widget: { template: "pills", flags: true }
    if (config.widget && typeof config.widget === "object") {
      var wo = config.widget;
      if (wo.template) config.widgetTemplate = wo.template;
      if (wo.container) config.widgetContainer = wo.container;
      if (wo.position) config.widgetPosition = wo.position;
      if (wo.theme) config.widgetTheme = wo.theme;
      if (wo.flags != null) config.widgetFlags = wo.flags;
      if (wo.compact != null) config.widgetCompact = wo.compact;
      if (wo.showLabel != null) config.widgetShowLabel = wo.showLabel;
      config.widget = true;
    }

    // Resolve apiBase into endpoints
    if (config.apiBase) {
      const base = config.apiBase.replace(/\/$/, "");
      if (!userConfig.apiEndpoint) config.apiEndpoint = base + "/api/translate";
      if (!userConfig.streamEndpoint) config.streamEndpoint = base + "/api/translate-stream";
    }

    // Restore persisted language preference, or auto-detect
    var savedLang = null;
    try { savedLang = localStorage.getItem("lt_lang"); } catch {}

    if (savedLang && savedLang !== config.sourceLanguage) {
      config.targetLanguage = savedLang;
      currentLanguage = savedLang;
    } else if (!config.targetLanguage) {
      config.targetLanguage = detectBrowserLanguage();
      currentLanguage = config.targetLanguage;
    }

    currentLanguage = currentLanguage || config.sourceLanguage;

    initialized = true;
    fireInitializedEvent();

    // Create floating widget
    const mount = () => {
      createWidget();
      updateWidgetState(currentLanguage);
    };
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", mount);
    } else {
      mount();
    }

    // Auto-translate if configured
    if (config.autoTranslate !== false && config.targetLanguage !== config.sourceLanguage) {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => translate());
      } else {
        translate();
      }
    }

    // Watch for dynamically loaded content (SPAs, AJAX-loaded pages)
    const startObs = () => startObserver();
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", startObs);
    } else {
      startObs();
    }
  }

  /**
   * Fire custom events
   */
  function fireInitializedEvent() {
    const event = new CustomEvent("localize:initialized", {
      detail: { language: currentLanguage }
    });
    document.dispatchEvent(event);
  }

  function fireTranslatedEvent() {
    const event = new CustomEvent("localize:translated", {
      detail: { language: currentLanguage }
    });
    document.dispatchEvent(event);
  }

  function restoreOriginals() {
    for (const [node, html] of originalTexts) {
      node.innerHTML = html;
      node.classList.remove("lt-pending", "lt-streaming", "lt-done");
    }
    for (const [, entry] of originalAttrs) {
      entry.node.setAttribute(entry.attr, entry.value);
    }
  }

  function untranslate() {
    restoreOriginals();
    currentLanguage = config.sourceLanguage;
    config.targetLanguage = config.sourceLanguage;
    try { localStorage.removeItem("lt_lang"); } catch {}
    updateWidgetState(config.sourceLanguage);
    updateWidgetProgress(0);
    document.dispatchEvent(new CustomEvent("localize:translated", {
      detail: { language: config.sourceLanguage }
    }));
  }

  /**
   * Clear translation cache
   */
  function clearCache() {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith("lt_cache:")) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Get cache statistics
   */
  function getCacheStats() {
    const stats = { total: 0, expired: 0, valid: 0 };
    const keys = Object.keys(localStorage);
    const now = Date.now();

    keys.forEach(key => {
      if (key.startsWith("lt_cache:")) {
        stats.total++;
        try {
          const entry = JSON.parse(localStorage.getItem(key));
          if (entry.expiresAt && entry.expiresAt < now) {
            stats.expired++;
          } else {
            stats.valid++;
          }
        } catch {
          stats.expired++;
        }
      }
    });

    return stats;
  }

  // Public API (Localize.js compatible)
  return {
    initialize,
    translate,
    setLanguage,
    getLanguage,
    untranslate,
    clearCache,
    getCacheStats,
    version: "2.0.0",
  };

}));
